import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';

/**
 * The change orders on a bid, one line each, where the bid says who signed it.
 *
 * The owner, Oct 6 2026: "can you allow me to make change orders on bids? So if
 * we are going to change a bid, but it's already accepted, click change order
 * so I can change something on the bid and then resend."
 *
 * What they first signed is never rewritten, so this is the record of
 * everything agreed after it: each change order's line (the server's own words
 * — services/changeOrders.historyWords), and under a tap what the bid was
 * before, what changed and what it is after. One the customer approved that
 * could not go on the calendar carries a Try again button.
 *
 * Anyone who can see the bid can read this (a bidder too); only the owner gets
 * the buttons, and the routes refuse anyone else.
 */

type Row = { key?: string; op: string; words: string; lands?: string | null };
type Order = {
  id: number; number: number; status: string; words: string; reason: string;
  applied?: { rows?: Row[]; message?: string } | null; apply_error?: string | null;
  decline_note?: string | null; office_note?: string | null;
  before?: { lines?: any[] } | null; after?: { lines?: any[] } | null;
};

const usd = (n: number) => `$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const priceOf = (l: any) => `${usd(Number(l.total) || (Number(l.quantity) || 1) * (Number(l.unit_price) || 0))}${l.unit === 'visit' ? ' a visit' : l.unit === 'month' ? ' a month' : l.unit === 'year' ? ' a year' : ''}`;
const onBid = (lines?: any[]) => (lines || []).filter(l => l.is_selected !== 0 && l.is_selected !== false);
const OPEN = ['draft', 'sent', 'approved'];

export default function ChangeOrderHistory({ quoteId, orders, canWrite = false, onOpen, onChanged }: {
  quoteId: number; orders?: { open: Order | null; history: Order[] } | null; canWrite?: boolean; onOpen: () => void; onChanged: () => void;
}) {
  const [shown, setShown] = useState<number | null>(null);
  const [trying, setTrying] = useState<number | null>(null);
  const history = orders?.history || [];
  // What the bid was before and after each one is only fetched when a line is opened.
  const { data: full } = useQuery(['quote-change-orders', quoteId, history.map(h => `${h.id}:${h.status}`).join(',')],
    () => api.get(`/quotes/${quoteId}/change-orders`).then(r => r.data as { history: Order[] }), { enabled: shown != null });
  // One that was never applied keeps no "where it landed" rows; its rows are read from the same preview the sheet uses.
  const shownOrder = history.find(h => h.id === shown) || null;
  const { data: rowsData } = useQuery(['change-order-rows', shown, shownOrder?.status],
    () => api.get(`/quotes/change-orders/${shown}/preview`).then(r => (r.data?.rows || []) as Row[]),
    { enabled: shown != null && !!shownOrder && !shownOrder.applied?.rows?.length });
  if (!history.length) return null;

  async function tryAgain(co: Order) {
    setTrying(co.id);
    try {
      const r = await api.post(`/quotes/change-orders/${co.id}/retry`, {});
      toast.success(r.data?.message || 'Change order applied.', { autoClose: 12000 });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'It is still not on the calendar — nothing was changed.', { autoClose: 10000 });
    } finally { setTrying(null); onChanged(); }
  }

  return (
    <div className="mb-3" data-testid="change-order-history">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Change orders</p>
      <ul className="space-y-1.5">
        {history.map(co => {
          const isShown = shown === co.id;
          const detail = isShown ? (full?.history || []).find(h => h.id === co.id) || null : null;
          const rows: Row[] = co.applied?.rows?.length ? co.applied.rows : (isShown ? rowsData || [] : []);
          const tone = co.status === 'approved' ? 'border-red-200 bg-red-50' : OPEN.includes(co.status) ? 'border-amber-200 bg-amber-50' : co.status === 'applied' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50';
          return (
            <li key={co.id} className={`rounded-lg border ${tone}`}>
              <button type="button" onClick={() => setShown(isShown ? null : co.id)} aria-expanded={isShown} className="w-full flex items-start justify-between gap-2 px-2.5 py-2 text-left">
                <span className="text-xs font-semibold text-gray-800">{co.words}</span>
                <ChevronDown className={`h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5 transition-transform ${isShown ? 'rotate-180' : ''}`} aria-hidden />
              </button>
              {(canWrite && OPEN.includes(co.status)) && (
                <div className="flex flex-wrap gap-2 px-2.5 pb-2">
                  {co.status === 'approved' && (
                    <button type="button" onClick={() => tryAgain(co)} disabled={trying != null} className="btn-primary text-xs py-1.5 px-3 disabled:opacity-50">{trying === co.id ? 'Trying…' : 'Try again'}</button>
                  )}
                  <button type="button" onClick={onOpen} className="text-xs font-semibold text-brand-800 border border-brand-300 bg-white rounded-lg px-3 py-1.5">Open it</button>
                </div>
              )}
              {isShown && (
                <div className="px-2.5 pb-2.5 space-y-2 text-xs text-gray-700 border-t border-black/5 pt-2">
                  {co.reason && <p><span className="font-semibold">Why:</span> {co.reason}</p>}
                  {co.office_note && <p><span className="font-semibold">Note:</span> {co.office_note}</p>}
                  {co.decline_note && <p><span className="font-semibold">They wrote:</span> “{co.decline_note}”</p>}
                  <div>
                    <p className="font-semibold text-gray-500 uppercase tracking-wide text-[10px] mb-0.5">Before</p>
                    {!detail ? <p className="text-gray-500">Loading…</p> : (
                      <ul>{onBid(detail.before?.lines).map((l: any) => <li key={l.id} className="flex justify-between gap-2"><span>{l.description}</span><span className="whitespace-nowrap">{priceOf(l)}</span></li>)}</ul>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-500 uppercase tracking-wide text-[10px] mb-0.5">The change</p>
                    {rows.length ? (
                      <ul className="space-y-0.5">{rows.map((r, i) => <li key={r.key || i}>{r.words}{co.status === 'applied' && r.lands ? <span className="text-gray-500"> → {r.lands}</span> : null}</li>)}</ul>
                    ) : <p className="text-gray-500">{co.status === 'draft' ? 'Nothing in it yet.' : 'Loading…'}</p>}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-500 uppercase tracking-wide text-[10px] mb-0.5">After</p>
                    {co.status !== 'applied' ? <p className="text-gray-500">Not applied — the bid is as it was.</p>
                      : !detail ? <p className="text-gray-500">Loading…</p> : (
                        <ul>{onBid(detail.after?.lines).map((l: any) => <li key={l.id} className="flex justify-between gap-2"><span>{l.description}</span><span className="whitespace-nowrap">{priceOf(l)}</span></li>)}</ul>
                      )}
                  </div>
                  {co.status === 'applied' && co.applied?.message && <p className="text-gray-500">{co.applied.message}</p>}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
